/**
 * Boundary mapping between the user-facing Position label and the GraphQL
 * `UserPosition` enum (TRK-6072).
 *
 * The API stores and returns `position` as a display string (e.g. the User
 * output field `position` is `"Treatment Coordinator"`), but the mutation input
 * `UpdateUserInput.position` is the `UserPosition` enum with SCREAMING_SNAKE
 * values (e.g. `TREATMENT_COORDINATOR`). The activation screen and the
 * Settings > Users > Details form both collect the display label, so they must
 * convert it to the enum before calling `updateMe` / `updateUser`.
 */
import { UserPosition } from 'app/generated/graphql'

/** Display label -> `UserPosition` enum value. */
const LABEL_TO_ENUM: Record<string, UserPosition> = {
  Executive: UserPosition.Executive,
  Consultant: UserPosition.Consultant,
  Dentist: UserPosition.Dentist,
  Hygienist: UserPosition.Hygienist,
  Manager: UserPosition.Manager,
  'Treatment Coordinator': UserPosition.TreatmentCoordinator,
  Administrator: UserPosition.Administrator,
  Other: UserPosition.Other,
}

/**
 * Convert a Position display label to the `UserPosition` enum for a mutation
 * input. Returns `undefined` for an empty, missing, or unrecognized label so
 * the caller can omit `position` rather than send an invalid enum value.
 */
export function toUserPosition(value: unknown): UserPosition | undefined {
  if (typeof value !== `string`) return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  return LABEL_TO_ENUM[trimmed]
}
